import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { imageUrl, formatApiErrorDetail } from "@/lib/api";
import { ArrowLeft, Upload, X, Plus, Trash2 } from "lucide-react";

const CONDITIONS = ["New", "Like New", "Excellent", "Very Good", "Good", "Fair", "For Parts / Repair"];
const STATUSES = ["available", "reserved", "sold", "hidden"];

const emptyProduct = {
  sku: "", name: "", category: "Laptops", subcategory: "", brand: "", model: "",
  price: 0, cost: "", stock_quantity: 1, condition: "Good", status: "available",
  description: "", images: [], featured: false, specs: {},
  warranty: "", included_accessories: [], serial_number: "", internal_notes: "",
  low_stock_threshold: 2,
};

export default function AdminProductForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyProduct);
  const [categories, setCategories] = useState([]);
  const [specRows, setSpecRows] = useState([]);
  const [accessoryInput, setAccessoryInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/categories");
      setCategories(data);
    })();
  }, []);

  useEffect(() => {
    if (!isEdit) { setSpecRows([]); return; }
    (async () => {
      try {
        const { data } = await api.get(`/products/${id}`);
        setForm({ ...emptyProduct, ...data, cost: data.cost ?? "", serial_number: data.serial_number ?? "", internal_notes: data.internal_notes ?? "" });
        setSpecRows(Object.entries(data.specs || {}).map(([k, v]) => ({ k, v: String(v) })));
      } catch { toast.error("Failed to load product."); }
    })();
  }, [id, isEdit]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/products/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, images: [...f.images, data.url] }));
      toast.success("Image uploaded.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed.");
    }
    setUploading(false);
  };

  const onFiles = (files) => { Array.from(files).forEach(uploadImage); };

  const removeImage = (i) => setForm(f => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));

  const addSpec = () => setSpecRows(r => [...r, { k: "", v: "" }]);
  const setSpec = (i, k, v) => setSpecRows(r => r.map((row, idx) => idx === i ? { k, v } : row));
  const removeSpec = (i) => setSpecRows(r => r.filter((_, idx) => idx !== i));

  const addAccessory = () => {
    if (!accessoryInput.trim()) return;
    setForm(f => ({ ...f, included_accessories: [...f.included_accessories, accessoryInput.trim()] }));
    setAccessoryInput("");
  };
  const removeAccessory = (i) => setForm(f => ({ ...f, included_accessories: f.included_accessories.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const specs = {};
    specRows.forEach(({ k, v }) => { if (k.trim()) specs[k.trim()] = v; });
    const payload = {
      ...form,
      price: Number(form.price) || 0,
      cost: form.cost === "" ? null : Number(form.cost),
      stock_quantity: Number(form.stock_quantity) || 0,
      low_stock_threshold: Number(form.low_stock_threshold) || 2,
      specs,
    };
    try {
      if (isEdit) {
        await api.patch(`/products/${id}`, payload);
        toast.success("Product updated.");
      } else {
        await api.post("/products", payload);
        toast.success("Product published.");
      }
      navigate("/admin/inventory");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed.");
    }
    setSaving(false);
  };

  return (
    <form onSubmit={submit} data-testid="admin-product-form">
      <Link to="/admin/inventory" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Back to inventory
      </Link>

      <div className="mt-4 flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-blue-600">{isEdit ? "Edit" : "New"}</div>
          <h1 className="mt-1 font-display text-3xl font-bold">{isEdit ? "Edit product" : "Add new product"}</h1>
        </div>
        <button disabled={saving} data-testid="admin-product-save" className="px-5 py-2.5 rounded-full bg-slate-900 hover:bg-blue-600 disabled:opacity-60 text-white font-semibold text-sm">
          {saving ? "Saving…" : (isEdit ? "Save changes" : "Publish product")}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card title="Basics">
            <Row>
              <Field label="Name" required>
                <input required data-testid="pf-name" value={form.name} onChange={(e) => update("name", e.target.value)} className="input" />
              </Field>
            </Row>
            <Row cols={2}>
              <Field label="SKU" required>
                <input required data-testid="pf-sku" value={form.sku} onChange={(e) => update("sku", e.target.value)} className="input font-mono" />
              </Field>
              <Field label="Category" required>
                <select required data-testid="pf-category" value={form.category} onChange={(e) => update("category", e.target.value)} className="input">
                  {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </Field>
            </Row>
            <Row cols={2}>
              <Field label="Brand" required>
                <input required data-testid="pf-brand" value={form.brand} onChange={(e) => update("brand", e.target.value)} className="input" />
              </Field>
              <Field label="Model">
                <input data-testid="pf-model" value={form.model || ""} onChange={(e) => update("model", e.target.value)} className="input" />
              </Field>
            </Row>
            <Field label="Description">
              <textarea rows={4} data-testid="pf-description" value={form.description} onChange={(e) => update("description", e.target.value)} className="input" />
            </Field>
          </Card>

          <Card title="Images">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {form.images.map((img, i) => (
                <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 bg-slate-50" data-testid={`pf-image-${i}`}>
                  <img src={imageUrl(img)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeImage(i)} data-testid={`pf-image-remove-${i}`} className="absolute top-1 right-1 w-7 h-7 rounded-full bg-white/95 border border-slate-200 grid place-items-center text-rose-600 hover:bg-rose-50">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                data-testid="pf-upload-image"
                className="aspect-square rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 grid place-items-center text-slate-500 text-xs font-medium"
              >
                {uploading ? "Uploading…" : (<><Upload className="w-5 h-5 mb-1" /> Upload</>)}
              </button>
              <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => onFiles(e.target.files)} data-testid="pf-file-input" />
            </div>
            <p className="mt-3 text-xs text-slate-500">JPG, PNG, or WebP. Max 8MB each. First image is the cover.</p>
          </Card>

          <Card title="Specifications">
            <div className="space-y-2">
              {specRows.map((row, i) => (
                <div key={i} className="grid grid-cols-[1fr_1.5fr_auto] gap-2" data-testid={`pf-spec-row-${i}`}>
                  <input placeholder="Field (e.g. Processor)" value={row.k} onChange={(e) => setSpec(i, e.target.value, row.v)} className="input" />
                  <input placeholder="Value" value={row.v} onChange={(e) => setSpec(i, row.k, e.target.value)} className="input" />
                  <button type="button" onClick={() => removeSpec(i)} className="p-2 rounded-lg hover:bg-rose-50 text-rose-600">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <button type="button" onClick={addSpec} data-testid="pf-add-spec" className="mt-3 inline-flex items-center gap-1.5 text-sm text-blue-600 font-medium">
              <Plus className="w-4 h-4" /> Add specification
            </button>
          </Card>

          <Card title="Included accessories">
            <div className="flex gap-2">
              <input value={accessoryInput} onChange={(e) => setAccessoryInput(e.target.value)} placeholder="e.g. Original charger" className="input flex-1" data-testid="pf-accessory-input" />
              <button type="button" onClick={addAccessory} className="px-4 rounded-lg bg-slate-900 text-white text-sm font-medium" data-testid="pf-accessory-add">Add</button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {form.included_accessories.map((a, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-sm">
                  {a} <button type="button" onClick={() => removeAccessory(i)} className="text-slate-400 hover:text-rose-600"><X className="w-3.5 h-3.5" /></button>
                </span>
              ))}
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card title="Pricing & stock">
            <Row cols={2}>
              <Field label="Price ($)" required>
                <input required type="number" step="0.01" min="0" data-testid="pf-price" value={form.price} onChange={(e) => update("price", e.target.value)} className="input" />
              </Field>
              <Field label="Cost ($)" hint="Admin-only">
                <input type="number" step="0.01" min="0" data-testid="pf-cost" value={form.cost} onChange={(e) => update("cost", e.target.value)} className="input" />
              </Field>
            </Row>
            <Row cols={2}>
              <Field label="Stock qty" required>
                <input required type="number" min="0" data-testid="pf-stock" value={form.stock_quantity} onChange={(e) => update("stock_quantity", e.target.value)} className="input" />
              </Field>
              <Field label="Low stock threshold">
                <input type="number" min="0" data-testid="pf-low-threshold" value={form.low_stock_threshold} onChange={(e) => update("low_stock_threshold", e.target.value)} className="input" />
              </Field>
            </Row>
          </Card>

          <Card title="Condition & status">
            <Field label="Condition">
              <select data-testid="pf-condition" value={form.condition} onChange={(e) => update("condition", e.target.value)} className="input">
                {CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select data-testid="pf-status" value={form.status} onChange={(e) => update("status", e.target.value)} className="input">
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <p className="mt-1 text-xs text-slate-500">Stock qty automatically drives available / low_stock / out_of_stock.</p>
            </Field>
            <label className="flex items-center gap-2 mt-2 text-sm">
              <input type="checkbox" data-testid="pf-featured" checked={form.featured} onChange={(e) => update("featured", e.target.checked)} className="rounded" />
              Featured product
            </label>
          </Card>

          <Card title="Warranty & internal">
            <Field label="Warranty">
              <input data-testid="pf-warranty" value={form.warranty || ""} onChange={(e) => update("warranty", e.target.value)} placeholder="e.g. 90-day limited" className="input" />
            </Field>
            <Field label="Serial number" hint="Admin-only, never displayed publicly">
              <input data-testid="pf-serial" value={form.serial_number || ""} onChange={(e) => update("serial_number", e.target.value)} className="input font-mono" />
            </Field>
            <Field label="Internal notes" hint="Admin-only">
              <textarea rows={3} data-testid="pf-notes" value={form.internal_notes || ""} onChange={(e) => update("internal_notes", e.target.value)} className="input" />
            </Field>
          </Card>
        </div>
      </div>

      <style>{`.input{width:100%;padding:0.6rem 0.85rem;border-radius:0.55rem;border:1px solid rgb(226 232 240);background:white;font-size:0.9rem;outline:none}.input:focus{border-color:rgb(37 99 235);box-shadow:0 0 0 2px rgba(37,99,235,0.15)}`}</style>
    </form>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5">
      <div className="font-display font-semibold mb-4">{title}</div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function Row({ children, cols = 1 }) { return <div className={`grid gap-4 ${cols === 2 ? "grid-cols-1 sm:grid-cols-2" : ""}`}>{children}</div>; }
function Field({ label, children, required, hint }) {
  return (
    <div>
      <label className="text-xs font-semibold tracking-wide uppercase text-slate-500 flex items-center gap-1">
        {label}{required && <span className="text-rose-500">*</span>}
        {hint && <span className="normal-case text-[10px] text-slate-400 font-normal ml-1">· {hint}</span>}
      </label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
